import { formatIsoDate } from '@/utils/dateUtils';
import type { SearchPoiResult } from '@/api/poisApi';

export type PoiStatus =
    | 'leak_found'
    | 'no_leak_found'
    | 'awaiting_assessment'
    | 'inconclusive'
    | 'already_marked_up'
    | 'unable_to_check'
    | 'unclassified';

/**
 * Normalises raw status strings from the API to a canonical PoiStatus.
 * 'unclassified' passes through — it exists precisely so unclassifiable data stays
 * visible on every surface, so mapping it to a plausible status here would defeat it.
 * Any other unrecognised value — including the retired 'no_dig_repairs', null, or
 * undefined — maps to 'awaiting_assessment' so the frontend never displays an unknown
 * status category.
 */
export function normalisePoiStatus(raw: string | null | undefined): PoiStatus {
    switch (raw) {
        case 'leak_found':
        case 'no_leak_found':
        case 'awaiting_assessment':
        case 'inconclusive':
        case 'already_marked_up':
        case 'unable_to_check':
        case 'unclassified':
            return raw;
        default:
            return 'awaiting_assessment';
    }
}

export interface PoiFeatureProperties {
    id: string;
    status: PoiStatus;
    /**
     * Entity discriminator for the split POI/leak contract. Optional because the legacy
     * response shape predates it: a feature without an entity_type is a POI entity
     * (normaliseEntityType in entityStatus.ts is the one place that rule lives).
     */
    entity_type?: 'poi' | 'leak';
    /** The platform.poi row backing this entity — used only for history fetches. */
    source_poi_id?: string;
    /** The family root's id — used only for family navigation affordances. */
    family_poi_id?: string;
    /** Leak entities only: "21-1" for child leaks; null for a root's derived first leak. */
    suffix_label?: string | null;
    name: string;
    group_id: string;
    stop: number;
    /**
     * Hierarchy context. Null where a POI resolves to no Area/DMA (the Unassigned groups) — these
     * are genuinely absent, not blank. No component renders them; they exist for context and
     * future map↔table sync, so a null is carried truthfully rather than faked into a string.
     */
    area: string | null;
    dma_ident: string | null;
    dma_name: string | null;
    is_planned: boolean;
    /** Nullable: `poi.what_three_words` is nullable. Every consumer already renders an em dash. */
    what3words: string | null;
    pipe_type: string | null;
    assumed_leakage_rate: number;
    measured_leakage_rate: number | null;
    leak_what3words?: string | null;
    surveyed_date?: string | null;
    search_date?: string | null;
    is_leak_visible?: boolean | null;
    nodig_fix?: boolean | null;
    repaired?: boolean | null;
    /**
     * The owning client. Present only on the Network Explorer's cross-client feed, where
     * records of several clients share one map; the single-client feeds never name the
     * client, so single-client surfaces must not depend on these.
     */
    client_id?: string;
    client_name?: string;
}

/**
 * Maps a camelCase SearchPoiResult (search API response) to snake_case PoiFeatureProperties
 * (GeoJSON layer). Under the entity contract the produced properties mirror the map
 * feature exactly: the id is the wire entity id ('leak:'-prefixed for child rows) so a
 * search pick highlights the same feature a map click would, and the status is the
 * split-model entity status (leak status for leak rows). Against a pre-split API the
 * entity fields are absent and the legacy mapping is unchanged.
 */
export function mapSearchPoiToFeatureProperties(poi: SearchPoiResult): PoiFeatureProperties {
    const isLeak = poi.entityType === 'leak';
    const entityStatus = isLeak ? poi.leakEntityStatus : poi.poiEntityStatus;
    return {
        id: poi.entityId ?? poi.id,
        status: (entityStatus as PoiStatus | null | undefined) ?? normalisePoiStatus(poi.status),
        entity_type: poi.entityType === undefined ? undefined : isLeak ? 'leak' : 'poi',
        source_poi_id: poi.entityType === undefined ? undefined : poi.id,
        family_poi_id: poi.familyPoiId,
        suffix_label: poi.suffixLabel,
        name: poi.name,
        group_id: poi.groupId,
        stop: poi.stop,
        area: poi.area,
        dma_ident: poi.dmaIdent,
        dma_name: poi.dmaName,
        is_planned: poi.isPlanned,
        what3words: poi.what3Words,
        pipe_type: poi.pipeType,
        assumed_leakage_rate: poi.assumedLeakageRate,
        measured_leakage_rate: poi.measuredLeakageRate,
        leak_what3words: poi.leakWhat3Words,
        surveyed_date: poi.surveyedDate,
        search_date: poi.searchDate,
        is_leak_visible: poi.isLeakVisible,
        nodig_fix: poi.nodigFix,
        repaired: poi.repaired,
    };
}

export interface LeakageRateDisplay {
    value: number;
    provenance: 'Measured' | 'Assumed';
}

/**
 * Returns measured_leakage_rate with provenance 'Measured' when it is greater than 0; otherwise
 * assumed_leakage_rate with provenance 'Assumed'. A measured 0 falls back to assumed, matching the
 * server's platform.calculate_daily_leakage. Uses loose `!= null` because MapLibre feature
 * properties surface a missing measured rate as `undefined` (not `null`), which must also fall back.
 * Rates are raw l/s — pass the value through formatLeakageRateMlD for display.
 */
export function getLeakageRateDisplay(props: PoiFeatureProperties): LeakageRateDisplay {
    if (props.measured_leakage_rate != null && props.measured_leakage_rate > 0) {
        return { value: props.measured_leakage_rate, provenance: 'Measured' };
    }

    return { value: props.assumed_leakage_rate, provenance: 'Assumed' };
}

/**
 * Converts a single leakage rate from l/s (the stored unit) to Ml/D for display, to three decimal
 * places: × 86,400 s/day ÷ 1,000,000 l/Ml, e.g. 4 l/s → "0.346". Zero formats as "0.000", not a dash.
 * For individual rates only — the stats totals arrive in litres/day and are formatted by formatStat.
 */
export function formatLeakageRateMlD(ratePerSecond: number): string {
    return ((ratePerSecond * 86_400) / 1_000_000).toFixed(3);
}

/** Converts ISO "yyyy-MM-dd" to "DD/MM/YYYY" display format, or null when absent/empty. */
export function formatSearchDate(date: string | null | undefined): string | null {
    return formatIsoDate(date);
}

/** Returns "Yes" / "No", or null when the field is absent. Maps C# `Fixed` (repaired) to display text. */
export function formatRepaired(fixed: boolean | null | undefined): string | null {
    if (fixed === null || fixed === undefined) return null;
    return fixed ? 'Yes' : 'No';
}

/**
 * The canonical what3words map URL for an address — the ONE definition of the host.
 *
 * Every caller goes through here (the POI details panel, the hover cards' What3WordsField and the
 * Network Overview table's summary grid), so a change to the host cannot leave one surface with a
 * working link and another with a broken one.
 */
export function what3WordsUrl(value: string): string {
    return `https://map.what3words.com/${value}`;
}

/** Title-cases a snake_case pipe type string, or returns null when the value is null/empty. */
export function formatPipeType(pipeType: string | null | undefined): string | null {
    if (!pipeType) return null;
    return pipeType
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}
